import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Phone,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Smile,
  CalendarIcon,
  RefreshCw,
  Download,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
const DashboardPage = () => {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [wordCloud, setWordCloud] = useState({ general: [], categories: {} });
  const [selectedTopic, setSelectedTopic] = useState("general");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const fetchData = async () => {
    try {
      const dateFrom = format(dateRange.from, "yyyy-MM-dd 00:00:00");
      const dateTo = format(dateRange.to, "yyyy-MM-dd 23:59:59");

      const [statsRes, activityRes, wordCloudRes] = await Promise.all([
        authAxios.get(`/dashboard/stats?date_from=${dateFrom}&date_to=${dateTo}`),
        authAxios.get(`/dashboard/activity?date_from=${dateFrom}&date_to=${dateTo}`),
        authAxios.get(`/dashboard/word-cloud?date_from=${dateFrom}&date_to=${dateTo}`),
      ]);

      setStats(statsRes.data);
      setActivity(activityRes.data);
      setWordCloud(wordCloudRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const dateFrom = format(dateRange.from, "yyyy-MM-dd 00:00:00");
      const dateTo = format(dateRange.to, "yyyy-MM-dd 23:59:59");
      await authAxios.post(`/ucontact/sync-records?date_from=${dateFrom}&date_to=${dateTo}`);
      toast.success("Sincronización iniciada");
      setTimeout(fetchData, 3000);
    } catch (error) {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    try {
      const dateFrom = format(dateRange.from, "yyyy-MM-dd 00:00:00");
      const dateTo = format(dateRange.to, "yyyy-MM-dd 23:59:59");
      const response = await authAxios.get(
        `/reports/export?date_from=${dateFrom}&date_to=${dateTo}&format=csv`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pioqa_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Reporte descargado");
    } catch (error) {
      toast.error("Error al exportar reporte");
    }
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "sky" }) => (
    <Card className="kpi-card card-hover" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className={`text-3xl font-bold mt-2 text-${color}-600`}>{value}</p>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
          {trend !== undefined && (
            <div className={`kpi-change mt-2 flex items-center text-xs font-medium ${trend >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'} px-2 py-1 rounded-full w-fit`}>
              {trend >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
              <span>{Math.abs(trendValue || trend)}% vs ant.</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </Card>
  );

  const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Calculate trends
  const callsTrend = stats?.prev_total_calls ? Math.round(((stats.total_calls - stats.prev_total_calls) / stats.prev_total_calls) * 100) : 0;
  const durationTrend = stats?.prev_total_duration_hours ? Math.round(((stats.total_duration_hours - stats.prev_total_duration_hours) / stats.prev_total_duration_hours) * 100) : 0;
  const sentimentTrend = stats?.prev_avg_sentiment ? Math.round(((stats.avg_sentiment - stats.prev_avg_sentiment) / stats.prev_avg_sentiment) * 100) : 0;
  
  const COLORS = ['#10B981', '#F43F5E', '#94A3B8'];
  const sentimentData = [
    { name: 'Positivo', value: 60 },
    { name: 'Negativo', value: 20 },
    { name: 'Neutral', value: 20 },
  ]; // Placeholder for Donut chart, can be replaced by real distribution later if API provides it

  const anatomyData = [
    { name: 'Conversación', value: 100 - (stats?.silence_percentage || 0) - (stats?.music_percentage || 0) },
    { name: 'Silencio', value: stats?.silence_percentage || 0 },
    { name: 'Música/Ruido', value: stats?.music_percentage || 0 },
  ];
  const ANATOMY_COLORS = ['#3B82F6', '#CBD5E1', '#F59E0B'];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Resumen de actividad y métricas globales</p>
        </div>

        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2" data-testid="date-filter-btn">
                <CalendarIcon className="w-4 h-4" />
                {format(dateRange.from, "dd MMM", { locale: es })} - {format(dateRange.to, "dd MMM", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => range && setDateRange(range)}
                numberOfMonths={2}
                locale={es}
              />
            </PopoverContent>
          </Popover>

          <Button
            onClick={handleSync}
            disabled={syncing}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="sync-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sincronizar
          </Button>

          <Button
            onClick={handleExport}
            variant="outline"
            className="border-orange-200 text-orange-600 hover:bg-orange-50"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <KPICard
          title="Llamadas Totales"
          value={stats?.total_calls?.toLocaleString() || "0"}
          subtitle={`${stats?.analyzed_calls || 0} analizadas`}
          icon={Phone}
          color="sky"
          trend={callsTrend}
        />
        <KPICard
          title="Duración Total"
          value={`${stats?.total_duration_hours?.toFixed(1) || "0"} h`}
          icon={Clock}
          color="slate"
          trend={durationTrend}
        />
        <KPICard
          title="Duración Promedio"
          value={`${stats?.avg_duration_minutes?.toFixed(1) || "0"} m`}
          icon={Clock}
          color="slate"
        />
        <KPICard
          title="Sentimiento Promedio"
          value={stats?.avg_sentiment?.toFixed(1) || "5.0"}
          icon={Smile}
          color="sky"
          trend={sentimentTrend}
        />
        <KPICard
          title="Llamadas de Riesgo"
          value={`${stats?.risk_percentage?.toFixed(1) || "0"}%`}
          subtitle={`${stats?.risk_calls || 0} llamadas`}
          icon={AlertTriangle}
          color="orange"
        />
      </div>

      {/* Deep Analysis & Anatomy Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sentiment Distribution */}
        <Card data-testid="sentiment-donut">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Sentimiento General (IA)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sentimentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {sentimentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2 px-4">
              <span>Apertura Promedio: <strong className="text-slate-800">{stats?.avg_opening_sentiment?.toFixed(1)}</strong></span>
              <span>Cierre Promedio: <strong className="text-slate-800">{stats?.avg_closing_sentiment?.toFixed(1)}</strong></span>
            </div>
          </CardContent>
        </Card>
        
        {/* Anatomy of Call */}
        <Card data-testid="anatomy-donut">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Anatomía de Llamada</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={anatomyData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {anatomyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={ANATOMY_COLORS[index % ANATOMY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart */}
        <Card className="lg:col-span-2" data-testid="activity-chart">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Actividad Histórica</CardTitle>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-sky-500" />
                  <span className="text-slate-600">Llamadas</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={activity?.daily || []}>
                  <defs>
                    <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="_id" 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickFormatter={(value) => value?.slice(5) || ''}
                  />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="calls"
                    stroke="#0EA5E9"
                    strokeWidth={2}
                    fill="url(#colorCalls)"
                    dot={{ fill: '#0EA5E9', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#0284C7' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* By Day of Week */}
        <Card data-testid="weekday-chart">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Por Día de Semana</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activity?.by_weekday?.map((d, i) => ({
                  ...d,
                  name: weekDays[d._id - 1] || weekDays[i]
                })) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="calls" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Word Cloud and Hour Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Word Cloud */}
        <Card data-testid="word-cloud-card" className="lg:col-span-2">
          <CardHeader className="pb-4 border-b border-slate-100 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Filtro de nube de palabras</CardTitle>
              <p className="text-sm text-slate-500">Temas Detectados</p>
            </div>
            <div className="text-sm text-slate-500">
              Frases detectadas: {(selectedTopic === "general" ? wordCloud?.general : wordCloud?.categories?.[selectedTopic])?.length || 0}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row min-h-[300px]">
              {/* Sidebar */}
              <div className="w-full md:w-64 border-r border-slate-100 p-4 space-y-2 bg-slate-50/50">
                <button
                  onClick={() => setSelectedTopic("general")}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedTopic === "general" ? "bg-sky-100 text-sky-700" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  Todas las palabras
                </button>
                {Object.keys(wordCloud?.categories || {}).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedTopic(cat)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedTopic === cat ? "bg-orange-100 text-orange-700" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              
              {/* Cloud Area */}
              <div className="flex-1 p-6 flex flex-wrap items-center justify-center gap-3 bg-white">
                {(() => {
                  const currentWords = selectedTopic === "general" ? (wordCloud?.general || []) : (wordCloud?.categories?.[selectedTopic] || []);
                  return currentWords.length > 0 ? (
                    currentWords.map((word, i) => {
                      const size = Math.max(14, Math.min(36, 14 + word.count * 2.5));
                      const colors = [
                        'text-sky-600', 'text-emerald-500', 'text-orange-500', 
                        'text-rose-500', 'text-indigo-500', 'text-amber-500', 'text-teal-500'
                      ];
                      // Vocabulario inapropiado words should be mostly red/orange
                      const colorClass = selectedTopic === "Vocabulario Inapropiado" 
                        ? (i % 2 === 0 ? 'text-rose-600' : 'text-red-500')
                        : colors[i % colors.length];
                        
                      return (
                        <button
                          key={word.word}
                          onClick={() => navigate(`/calls?search=${encodeURIComponent(word.word)}`)}
                          className={`word-cloud-item ${colorClass} font-medium hover:opacity-70 transition-opacity cursor-pointer`}
                          style={{ fontSize: `${size}px` }}
                          title={`Aparece ${word.count} veces. Clic para ver llamadas.`}
                        >
                          {word.word}
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-slate-400">No hay datos suficientes para este filtro</p>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* By Hour */}
        <Card data-testid="hour-chart" className="lg:col-span-2">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Actividad por Hora</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activity?.by_hour || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="_id" tick={{ fontSize: 10, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label) => `${label}:00`}
                  />
                  <Bar dataKey="calls" fill="#F97316" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
